// src/apiServices/authService.js

import axios from 'axios';
import { APIUserLogin, APIUserLogout, BASE_URL } from './APIs';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const USER_INFO_KEY = 'userInfo';
const USER_PERMISSIONS_KEY = 'userPermissions';

class AuthService {
    constructor() {
        this.api = axios.create({
            baseURL: BASE_URL,
            timeout: 30000,
            headers: {
                'Accept': 'application/json',
                // 'Content-Type': 'application/json',
                "Content-Type": "multipart/form-data",
            }
        });

        this.refreshTokenPromise = null;
        this.isRefreshing = false;
        this.failedQueue = [];

        // Request interceptor to add token
        this.api.interceptors.request.use(
            (config) => {
                const token = this.getAccessToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor to handle token refresh
        this.api.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Handle 401 errors
                if (error.response?.status === 401 && !originalRequest._retry) {
                    const errorCode = error.response?.data?.code;
                    
                    // Token expired - attempt refresh
                    if (errorCode === 'TOKEN_EXPIRED') {
                        originalRequest._retry = true;
                        
                        try {
                            const newToken = await this.refreshAccessToken();
                            if (newToken) {
                                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                                return this.api(originalRequest);
                            }
                        } catch (refreshError) {
                            this.clearAllData();
                            window.location.href = '/login';
                            return Promise.reject(refreshError);
                        }
                    } 
                    // Invalid token or no token - redirect to login
                    else if (errorCode === 'TOKEN_INVALID' || errorCode === 'TOKEN_NOT_PROVIDED') {
                        this.clearAllData();
                        window.location.href = '/login';
                        return Promise.reject(error);
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    /**
     * Process queue of failed requests
     */
    processQueue(error, token = null) {
        this.failedQueue.forEach(promise => {
            if (error) {
                promise.reject(error);
            } else {
                promise.resolve(token);
            }
        });
        this.failedQueue = [];
    }

    /**
     * Refresh access token using refresh token
     */
    async refreshAccessToken() {
        if (this.isRefreshing) {
            // Wait for the refresh to complete
            return new Promise((resolve, reject) => {
                this.failedQueue.push({ resolve, reject });
            });
        }

        this.isRefreshing = true;
        this.refreshTokenPromise = null;

        try {
            const refreshToken = this.getRefreshToken();
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await axios.post(`${BASE_URL}/token/refresh`, {
                refresh_token: refreshToken
            });

            if (response.data.status === 'success') {
                const { access_token, refresh_token, expires_in } = response.data;
                
                // Store new tokens
                this.setTokens(access_token, refresh_token, expires_in);
                
                this.isRefreshing = false;
                this.processQueue(null, access_token);
                
                return access_token;
            } else {
                throw new Error('Refresh failed');
            }
        } catch (error) {
            this.isRefreshing = false;
            this.processQueue(error, null);
            this.clearAllData();
            throw error;
        }
    }

    /**
     * Store tokens
     */
    setTokens(accessToken, refreshToken, expiresIn) {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        
        // Calculate and store token expiry time
        const expiryTime = Date.now() + (expiresIn * 1000);
        sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    }

    /**
     * Get access token
     */
    getAccessToken() {
        return sessionStorage.getItem(ACCESS_TOKEN_KEY);
    }

    /**
     * Get refresh token
     */
    getRefreshToken() {
        return sessionStorage.getItem(REFRESH_TOKEN_KEY);
    }

    /**
     * Get token expiry
     */
    getTokenExpiry() {
        const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
        return expiry ? parseInt(expiry) : null;
    }

    /**
     * Check if token is expired
     */
    isTokenExpired() {
        const expiry = this.getTokenExpiry();
        if (!expiry) return true;
        return Date.now() >= expiry;
    }

    /**
     * Check if token needs refresh (5 minutes before expiry)
     */
    needsRefresh() {
        const expiry = this.getTokenExpiry();
        if (!expiry) return true;
        const timeUntilExpiry = expiry - Date.now();
        return timeUntilExpiry < 300000; // 5 minutes in milliseconds
    }

    /**
     * Login user
     */
    async login(email, password) {
        try {
            const response = await this.api.post(APIUserLogin, {
                email,
                password
            });

            if (response.data.status === 'success') {
                const { access_token, refresh_token, expires_in, data } = response.data;
                
                // Store tokens
                // this.setTokens(access_token, refresh_token, expires_in);
                // Store user info
                sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(data));
                sessionStorage.setItem(USER_PERMISSIONS_KEY,data.permissions);
                
                // Start auto refresh timer
                // this.startAutoRefreshTimer();

                return {
                    success: true,
                    data: data
                };
            } else {
                return {
                    success: false,
                    message: response.data.message
                };
            }
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed'
            };
        }
    }

    /**
     * Logout user
     */
    async logout() {
        try {
            // Clear auto refresh timer
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
            }

            const token = this.getAccessToken();
            if (token) {
                await this.api.post(APIUserLogout);
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.clearAllData();
        }
    }

    /**
     * Clear all stored data
     */
    clearAllData() {
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
        sessionStorage.removeItem(USER_INFO_KEY);
        sessionStorage.removeItem(USER_PERMISSIONS_KEY);
        
        // Also clear session storage if used
        sessionStorage.removeItem('userInfo');
        sessionStorage.removeItem('userPermissions');
    }
    /**
     * Get user ID
     */
    getUserId() {
        const user = this.getCurrentUser();
        return user ? user.id : null;
    }
    /**
     * Get user name
     */
    getUserName() {
        const user = this.getCurrentUser();
        return user ? user.name : null;
    }

    /**
     * Get user email
     */
    getUserEmail() {
        const user = this.getCurrentUser();
        return user ? user.email : null;
    }

    /**
     * Get current user from storage
     */
    getCurrentUser() {
        const userInfo = sessionStorage.getItem(USER_INFO_KEY);
        return userInfo ? JSON.parse(userInfo) : null;
    }

    /**
     * Get user permissions
     */
    getUserPermissions() {
        const permissions = sessionStorage.getItem(USER_PERMISSIONS_KEY);
        return permissions ? JSON.parse(permissions) : ['VIEW'];
    }

    /**
     * Check if user has specific permission
     */
    hasPermission(permission) {
        const permissions = this.getUserPermissions();
        return permissions.includes(permission) || permissions.includes('ALL');
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        // const token = this.getAccessToken();
        // return !!token && !this.isTokenExpired();
        const user = this.getCurrentUser();
        return !!user;
    }

    /**
     * Validate current token with server
     */
    async validateToken() {
        try {
            const response = await this.api.post('/token/validate');
            return response.data.status === 'success';
        } catch (error) {
            return false;
        }
    }

    /**
     * Auto refresh token if needed
     */
    async autoRefreshToken() {
        if (this.isAuthenticated() && this.needsRefresh()) {
            try {
                await this.refreshAccessToken();
                return true;
            } catch (error) {
                console.error('Auto refresh failed:', error);
                return false;
            }
        }
        return false;
    }

    /**
     * Start auto refresh timer
     */
    startAutoRefreshTimer() {
        // Clear existing timer
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
        // Check every minute
        this.refreshTimer = setInterval(async () => {
            if (this.isAuthenticated() && this.needsRefresh()) {
                await this.autoRefreshToken();
            }
        }, 60000); // 1 minute
    }

    /**
     * Get axios instance
     */
    getApi() {
        return this.api;
    }

    /**
     * Create an authenticated API call with automatic retry
     */
    async authenticatedRequest(config) {
        try {
            const response = await this.api(config);
            return response;
        } catch (error) {
            if (error.response?.status === 401) {
                // Token might be expired, try once more after refresh
                const newToken = await this.refreshAccessToken();
                if (newToken) {
                    config.headers = config.headers || {};
                    config.headers.Authorization = `Bearer ${newToken}`;
                    return this.api(config);
                }
            }
            throw error;
        }
    }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;

